import { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { colors } from '../styles/theme';
import { CoachScreen, type CoachClient } from './CoachScreen';
import type { ChatCopy } from './ChatScreen';
import { SoulThreadLoader } from '../components/SoulThreadLoader';
import { useRefreshingLoad } from '../hooks/useRefreshingLoad';
import { errorMessage } from '../utils/errorMessage';
import type { CoachTopic } from '../types/coach';
import type { ResultCard, ResultId, ResultsResponse } from '../types/results';
import images from '../data/resultImages.json';

export interface ResultsClient {
  load(): Promise<ResultsResponse>;
  /** Starts reading the personality profile the free results come from. */
  analyze(): Promise<void>;
  /** Takes the person to checkout for every other result. */
  unlock(): Promise<void>;
  /** Tries again to write paid readings that didn't come through. */
  retry(): Promise<void>;
  chat(topic: CoachTopic): CoachClient;
}

type Palette = { tint: string; edge: string; ink: string };
const palettes: Partial<Record<ResultId, Palette>> = {
  'sex-and-the-city': { tint: '#251225', edge: '#65334e', ink: '#f9a8d4' },
  hogwarts: { tint: '#24130f', edge: '#6b3a1f', ink: '#fbbf24' },
  'myers-briggs': { tint: '#151a36', edge: '#3b4a8c', ink: '#a5b4fc' },
  'big-five': { tint: '#0f2427', edge: '#1f5a5f', ink: '#5eead4' },
  friends: { tint: '#1f1530', edge: '#5b3b8c', ink: '#fde047' },
};
const paidPalette: Palette = { tint: colors.surface, edge: colors.surfaceLight, ink: '#f0abfc' };
const paletteFor = (id: ResultId) => palettes[id] ?? paidPalette;

// Results that compare someone with a character from a show or book.
const CHARACTER_RESULTS: ResultId[] = [
  'sex-and-the-city',
  'hogwarts',
  'friends',
  'the-office',
  'taylor-swift-era',
  'disney',
  'game-of-thrones',
];

// How long to watch for a payment after checkout before giving up on it arriving by itself.
const PAYMENT_WAIT_MS = 10 * 60_000;

const waitingText: Record<ResultCard['status'], string> = {
  ready: '',
  locked: '🔒  Unlock to see yours',
  preparing: 'Writing your reading…',
  failed: 'Couldn’t write this one yet',
};

const listed = (items: string[]) =>
  items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items[0];

const priceLabel = ({ amount, currency }: ResultsResponse['unlock']['price']) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(
    amount / 100
  );

type ResultImage = (typeof images)[keyof typeof images];

function CreditLink({ url, label }: { url: string; label: string }) {
  const linkProps =
    Platform.OS === 'web'
      ? { href: url, hrefAttrs: { target: '_blank', rel: 'noopener noreferrer' } }
      : { onPress: () => void Linking.openURL(url).catch(() => {}) };
  return (
    <Text
      {...linkProps}
      accessibilityRole="link"
      accessibilityLabel={`${label}: ${url}`}
      style={styles.creditLink}
    >
      {label}
    </Text>
  );
}

function Portrait({ asset, baseUrl }: { asset: ResultImage; baseUrl: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <View style={styles.portrait}>
      {failed ? (
        <Text style={styles.caption}>Photo unavailable</Text>
      ) : (
        <Image
          source={{ uri: `${baseUrl}/api/result-images/${asset.file}` }}
          style={styles.portraitImage}
          resizeMode="cover"
          accessibilityLabel={`Portrait of ${asset.actor}`}
          onError={() => setFailed(true)}
        />
      )}
      <Text style={styles.note}>
        {asset.actor} · Photo: {asset.credit}. <CreditLink url={asset.sourceUrl} label="Source" />
        {' · '}
        <CreditLink url={asset.licenseUrl} label={asset.license} />
        {' · Cropped. Actor portrait, not a scene from the show.'}
      </Text>
    </View>
  );
}

function ResultTile({ card, onPress }: { card: ResultCard; onPress: () => void }) {
  const palette = paletteFor(card.id);
  return (
    <TouchableOpacity
      style={[styles.tile, { backgroundColor: palette.tint, borderColor: palette.edge }]}
      activeOpacity={0.86}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={
        card.reading
          ? `${card.title}. ${card.question} ${card.reading.answer}. ${card.reading.tagline}.`
          : `${card.title}. ${card.question} ${waitingText[card.status]}.`
      }
    >
      <Text style={[styles.series, { color: palette.ink }]}>{card.title}</Text>
      <Text style={styles.question}>{card.question}</Text>
      {card.reading ? (
        <>
          <Text style={styles.answer}>{card.reading.answer}</Text>
          <Text style={[styles.tagline, { color: palette.ink }]}>{card.reading.tagline}</Text>
          <Text style={styles.line}>“{card.reading.line}”</Text>
        </>
      ) : (
        <Text style={styles.waiting}>{waitingText[card.status]}</Text>
      )}
    </TouchableOpacity>
  );
}

function UnlockPanel({
  data,
  busy,
  error,
  onUnlock,
}: {
  data: ResultsResponse;
  busy: boolean;
  error: string;
  onUnlock: () => void;
}) {
  const locked = data.results.filter((card) => card.status === 'locked');
  if (!locked.length) return null;
  const price = priceLabel(data.unlock.price);
  return (
    <View style={styles.unlock}>
      <Text style={styles.unlockTitle}>There’s more to you</Text>
      <Text style={styles.caption}>
        {locked.length} more results: {listed(locked.map((card) => card.title))}. Each one is
        written for you once you unlock them.
      </Text>
      {data.unlock.status === 'locked' ? (
        <TouchableOpacity
          style={[styles.action, busy && styles.disabled]}
          disabled={busy}
          onPress={onUnlock}
          accessibilityRole="button"
          accessibilityState={{ disabled: busy }}
        >
          <Text style={styles.actionText}>
            {busy ? 'Opening checkout…' : `Unlock everything · ${price}`}
          </Text>
        </TouchableOpacity>
      ) : (
        <Text style={styles.soon}>Coming soon</Text>
      )}
      {Boolean(error) && (
        <View accessibilityRole="alert">
          <Text style={styles.caption}>{error}</Text>
        </View>
      )}
    </View>
  );
}

function RetryPanel({
  busy,
  error,
  onRetry,
}: {
  busy: boolean;
  error: string;
  onRetry: () => void;
}) {
  return (
    <View style={styles.unlock}>
      <Text style={styles.caption}>Some of your results didn’t come through.</Text>
      <TouchableOpacity
        style={[styles.action, busy && styles.disabled]}
        disabled={busy}
        onPress={onRetry}
        accessibilityRole="button"
      >
        <Text style={styles.actionText}>{busy ? 'One moment…' : 'Try again'}</Text>
      </TouchableOpacity>
      {Boolean(error) && (
        <View accessibilityRole="alert">
          <Text style={styles.caption}>{error}</Text>
        </View>
      )}
    </View>
  );
}

function Reading({ card, imageBaseUrl }: { card: ResultCard; imageBaseUrl: string }) {
  const palette = paletteFor(card.id);
  const reading = card.reading;
  const asset =
    card.id === 'sex-and-the-city' && reading?.matchId
      ? (images as Record<string, ResultImage>)[reading.matchId]
      : undefined;
  return (
    <View style={[styles.reading, { backgroundColor: palette.tint, borderColor: palette.edge }]}>
      <Text style={[styles.series, { color: palette.ink }]}>{card.title}</Text>
      <Text style={styles.question}>{card.question}</Text>
      {reading ? (
        <>
          <Text style={styles.answer} accessibilityRole="header">
            {reading.answer}
          </Text>
          <Text style={[styles.tagline, { color: palette.ink }]}>{reading.tagline}</Text>
          {asset && <Portrait asset={asset} baseUrl={imageBaseUrl} />}
          <Text style={styles.description}>{reading.description}</Text>
          <Text style={[styles.quote, { color: palette.ink }]}>“{reading.line}”</Text>
          {reading.scores?.map((score) => (
            <View key={score.label} style={styles.score}>
              <View style={styles.scoreHeading}>
                <Text style={styles.scoreLabel}>{score.label}</Text>
                <Text style={styles.caption}>{score.value}/100</Text>
              </View>
              <View style={styles.track}>
                <View
                  style={[styles.fill, { width: `${score.value}%`, backgroundColor: palette.ink }]}
                />
              </View>
            </View>
          ))}
          <Text style={styles.note}>
            {CHARACTER_RESULTS.includes(card.id)
              ? 'Just for fun. An unofficial comparison, with no affiliation or endorsement.'
              : 'A playful AI reading of how you text, not a clinical assessment.'}
          </Text>
        </>
      ) : (
        <Text style={styles.waiting}>{waitingText[card.status]}</Text>
      )}
    </View>
  );
}

const resultCopy = (
  card: ResultCard & { reading: NonNullable<ResultCard['reading']> }
): ChatCopy => ({
  title: card.reading.answer,
  subtitle: `Your ${card.title} result.`,
  welcome: `Why ${card.reading.answer}?`,
  description: 'Ask what fits, what doesn’t, and what it says about you.',
  placeholder: `Ask about ${card.reading.answer}…`,
  suggestions: [
    ['Why this one', `Why am I ${card.reading.answer}?`],
    ['Where it doesn’t fit', `Where doesn’t ${card.reading.answer} fit me?`],
    ['In my messages', `Where does ${card.reading.answer} show up in how I text?`],
  ],
});

function ResultDetail({
  card,
  data,
  client,
  imageBaseUrl,
  busy,
  error,
  onBack,
  onUnlock,
  onRetry,
}: {
  card: ResultCard;
  data: ResultsResponse;
  client: ResultsClient;
  imageBaseUrl: string;
  busy: boolean;
  error: string;
  onBack: () => void;
  /** Left out while a payment that just happened is still landing. */
  onUnlock?: () => void;
  onRetry: () => void;
}) {
  const wide = useWindowDimensions().width >= 900;
  const ready = card.status === 'ready' && card.reading ? { ...card, reading: card.reading } : null;
  const chat = useMemo(
    () => (ready ? client.chat({ kind: 'result', id: card.id }) : null),
    // A new chat client only when the result or its availability changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, card.id, Boolean(ready)]
  );
  return (
    <View style={[styles.detail, wide && styles.detailWide]}>
      <ScrollView
        style={wide ? styles.readingPaneWide : styles.readingPane}
        contentContainerStyle={styles.readingContent}
      >
        <TouchableOpacity onPress={onBack} accessibilityRole="button">
          <Text style={styles.back}>← Your results</Text>
        </TouchableOpacity>
        <Reading card={card} imageBaseUrl={imageBaseUrl} />
        {onUnlock && card.status === 'locked' && (
          <UnlockPanel data={data} busy={busy} error={error} onUnlock={onUnlock} />
        )}
        {card.status === 'failed' && <RetryPanel busy={busy} error={error} onRetry={onRetry} />}
      </ScrollView>
      {chat && ready && (
        <View style={styles.chatPane}>
          <CoachScreen key={card.id} client={chat} copy={resultCopy(ready)} />
        </View>
      )}
    </View>
  );
}

export function ResultsScreen({
  client,
  selected,
  onSelect,
  onConnect,
  justUnlocked = false,
  imageBaseUrl = 'https://soulthread.me',
}: {
  client: ResultsClient;
  selected: ResultId | null;
  onSelect: (id: ResultId | null) => void;
  onConnect?: () => void;
  /** Back from checkout: the payment may land a moment after the person does. */
  justUnlocked?: boolean;
  imageBaseUrl?: string;
}) {
  const [refresh, setRefresh] = useState(0);
  const { data, error: loadError } = useRefreshingLoad(() => client.load(), [client, refresh]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // After checkout, watch for the payment, which lands on its own time, though not forever.
  const [paymentExpectedAt, setPaymentExpectedAt] = useState(() =>
    justUnlocked ? Date.now() : null
  );
  const locked = data?.unlock.status === 'locked';
  const awaitingPayment =
    locked && paymentExpectedAt !== null && Date.now() - paymentExpectedAt < PAYMENT_WAIT_MS;
  const paymentOverdue = locked && justUnlocked && !awaitingPayment;
  const waiting = Boolean(
    data &&
      (data.analysis === 'pending' ||
        data.results.some((card) => card.status === 'preparing') ||
        awaitingPayment)
  );
  // Keep an eye on results being prepared, without anyone having to reload.
  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => setRefresh((value) => value + 1), 15_000);
    return () => clearTimeout(timer);
  }, [waiting, data]);

  const unlock = () =>
    void act(async () => {
      await client.unlock();
      setPaymentExpectedAt(Date.now());
    });
  const act = async (action: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await action();
      setRefresh((value) => value + 1);
    } catch (failure) {
      setError(errorMessage(failure, 'Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (!data)
    return (
      <View style={styles.center}>
        {loadError ? (
          <>
            <Text style={styles.caption}>{loadError}</Text>
            <TouchableOpacity onPress={() => setRefresh((value) => value + 1)}>
              <Text style={styles.back}>Try again</Text>
            </TouchableOpacity>
          </>
        ) : (
          <ActivityIndicator color={colors.primary} />
        )}
      </View>
    );

  const card = selected && data.results.find((item) => item.id === selected);
  if (card)
    return (
      <ResultDetail
        card={card}
        data={data}
        client={client}
        imageBaseUrl={imageBaseUrl}
        busy={busy}
        error={error}
        onBack={() => onSelect(null)}
        onUnlock={justUnlocked ? undefined : unlock}
        onRetry={() => void act(client.retry)}
      />
    );

  const pending = data.analysis === 'pending';
  const enough = data.messageCount >= 20;
  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.heading}>
        <Text style={styles.eyebrow}>A FEW WAYS TO SEE YOURSELF</Text>
        <Text style={styles.title} accessibilityRole="header">
          Your results
        </Text>
        <Text style={styles.caption}>
          Part reflection. Part main-character energy. Entirely you.
        </Text>
      </View>
      {awaitingPayment && !justUnlocked && (
        <View accessibilityRole="alert">
          <Text style={styles.notice}>
            Finish checking out in your browser, and your new results will appear here.
          </Text>
        </View>
      )}
      {justUnlocked &&
        !paymentOverdue &&
        data.results.some((card) => card.status === 'locked' || card.status === 'preparing') && (
          <View accessibilityRole="alert">
            <Text style={styles.notice}>
              Thank you. Your new results are being written now and will appear here in a minute.
            </Text>
          </View>
        )}
      {paymentOverdue && (
        <View accessibilityRole="alert">
          <Text style={styles.notice}>
            Your payment hasn’t reached us yet. If you were charged, email support@soulthread.me and
            we’ll sort it out.
          </Text>
        </View>
      )}
      {data.results.length ? (
        <>
          <View style={styles.grid}>
            {data.results
              .filter((item) => item.tier === 'free')
              .map((item) => (
                <ResultTile key={item.id} card={item} onPress={() => onSelect(item.id)} />
              ))}
          </View>
          {!justUnlocked && <UnlockPanel data={data} busy={busy} error={error} onUnlock={unlock} />}
          {data.results.some((item) => item.status === 'failed') && (
            <RetryPanel busy={busy} error={error} onRetry={() => void act(client.retry)} />
          )}
          <View style={styles.grid}>
            {data.results
              .filter((item) => item.tier === 'paid')
              .map((item) => (
                <ResultTile key={item.id} card={item} onPress={() => onSelect(item.id)} />
              ))}
          </View>
          <Text style={styles.caption}>
            {data.messageCount.toLocaleString()} messages in your synced conversations.
            {data.analyzedAt
              ? ` Updated ${new Date(data.analyzedAt).toLocaleDateString()}.`
              : ''}{' '}
            These are playful AI readings, not clinical assessments or a fixed definition of you.
          </Text>
        </>
      ) : (
        <View style={styles.empty}>
          {pending ? (
            <SoulThreadLoader label="Finding your patterns" size={160} />
          ) : (
            <Text style={styles.emptyMark} aria-hidden>
              ◈
            </Text>
          )}
          <Text style={styles.emptyTitle}>
            {pending ? 'Your story is taking shape.' : 'Meet another side of yourself.'}
          </Text>
          <Text style={[styles.caption, styles.centered]}>
            {pending
              ? 'We’re finding the patterns in your conversations. Your results appear here when they’re ready.'
              : enough
                ? 'Your conversations are ready. Find your Hogwarts house, your type, your Sex and the City alter ego, and more.'
                : 'Bring at least 20 messages to find your Hogwarts house, your type, and more.'}
          </Text>
          {!pending && enough && (
            <TouchableOpacity
              style={[styles.action, styles.centeredAction, busy && styles.disabled]}
              disabled={busy}
              onPress={() => void act(client.analyze)}
              accessibilityRole="button"
            >
              <Text style={styles.actionText}>
                {busy ? 'Preparing…' : data.analysis === 'failed' ? 'Try again' : 'See my results'}
              </Text>
            </TouchableOpacity>
          )}
          {!pending && !enough && onConnect && (
            <TouchableOpacity
              style={[styles.action, styles.centeredAction]}
              onPress={onConnect}
              accessibilityRole="button"
            >
              <Text style={styles.actionText}>Connect your apps</Text>
            </TouchableOpacity>
          )}
          {Boolean(error) && (
            <View accessibilityRole="alert">
              <Text style={styles.caption}>{error}</Text>
            </View>
          )}
        </View>
      )}
    </ScrollView>
  );
}

const serif = Platform.OS === 'web' ? 'Playfair Display, Georgia, serif' : 'Georgia';
const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { width: '100%', maxWidth: 1040, alignSelf: 'center', padding: 32, gap: 20 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 32 },
  heading: { gap: 10, paddingBottom: 4 },
  eyebrow: { color: colors.textSecondary, fontSize: 11, fontWeight: '600', letterSpacing: 2 },
  title: { color: colors.text, fontSize: 36, fontWeight: '500', letterSpacing: -1 },
  caption: { color: colors.textSecondary, fontSize: 14, lineHeight: 22 },
  centered: { textAlign: 'center', maxWidth: 460 },
  notice: {
    color: colors.text,
    backgroundColor: colors.surfaceLight,
    borderRadius: 12,
    padding: 14,
    fontSize: 14,
  },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  tile: {
    flexGrow: 1,
    flexBasis: 280,
    maxWidth: 480,
    minHeight: 190,
    borderRadius: 20,
    borderWidth: 1,
    padding: 22,
    gap: 6,
  },
  series: { fontSize: 22, fontStyle: 'italic', fontFamily: serif, marginBottom: 6 },
  question: { color: colors.textSecondary, fontSize: 12, letterSpacing: 0.4 },
  answer: { color: colors.text, fontSize: 26, fontWeight: '600', letterSpacing: -0.5 },
  tagline: { fontSize: 14, fontWeight: '500' },
  line: {
    color: colors.textSecondary,
    fontSize: 13,
    fontStyle: 'italic',
    lineHeight: 20,
    marginTop: 8,
  },
  waiting: { color: colors.textSecondary, fontSize: 14, marginTop: 18 },
  unlock: {
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#7c3aed',
    backgroundColor: '#1d1036',
    padding: 24,
    gap: 12,
  },
  unlockTitle: { color: colors.text, fontSize: 22, fontFamily: serif, fontStyle: 'italic' },
  action: {
    alignSelf: 'flex-start',
    backgroundColor: colors.primary,
    paddingVertical: 12,
    paddingHorizontal: 22,
    borderRadius: 12,
  },
  actionText: { color: colors.text, fontSize: 14, fontWeight: '600' },
  centeredAction: { alignSelf: 'center' },
  disabled: { opacity: 0.6 },
  soon: { color: colors.textSecondary, fontSize: 14, fontWeight: '600' },
  detail: { flex: 1, minHeight: 0 },
  detailWide: { flexDirection: 'row' },
  readingPane: { maxHeight: '45%', flexGrow: 0 },
  readingPaneWide: {
    width: 400,
    flexGrow: 0,
    borderRightWidth: 1,
    borderRightColor: colors.surfaceLight,
  },
  readingContent: { padding: 24, gap: 16 },
  chatPane: { flex: 1, minWidth: 0, minHeight: 0 },
  back: { color: colors.textSecondary, fontSize: 13 },
  reading: { borderRadius: 20, borderWidth: 1, padding: 22, gap: 8 },
  description: { color: colors.text, fontSize: 14, lineHeight: 23, marginTop: 6 },
  quote: { fontSize: 14, fontStyle: 'italic', lineHeight: 22 },
  score: { gap: 6, marginTop: 6 },
  scoreHeading: { flexDirection: 'row', justifyContent: 'space-between' },
  scoreLabel: { color: colors.text, fontSize: 13 },
  track: { height: 6, borderRadius: 3, backgroundColor: colors.surfaceLight, overflow: 'hidden' },
  fill: { height: 6, borderRadius: 3 },
  note: { color: colors.textSecondary, fontSize: 11, lineHeight: 18, opacity: 0.85, marginTop: 6 },
  creditLink: { textDecorationLine: 'underline', color: colors.textSecondary },
  portrait: { gap: 8, marginVertical: 8 },
  portraitImage: { width: '100%', aspectRatio: 0.8, maxHeight: 320, borderRadius: 12 },
  empty: {
    alignItems: 'center',
    gap: 14,
    padding: 40,
    borderRadius: 20,
    backgroundColor: colors.surface,
  },
  emptyMark: { color: colors.textSecondary, fontSize: 40 },
  emptyTitle: { color: colors.text, fontSize: 22, fontWeight: '500' },
});
